﻿import { useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { formatNumber } from "../../../utilities/format";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { DateSelectCrumb } from "../../../components/Breadcrumbs/DateSelectCrumb";
import { ShiftSelectCrumb } from "../../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { ButtonCrumb } from "../../../components/Breadcrumbs/ButtonCrumb";
import { DataTable, TableRow } from "../../../components/DataTable/DataTable";
import { DataTableFilteredRequest, useDataTable } from "../../../components/DataTable/TableHook";
import { useAutoPageSize } from "../../../components/Hooks/useAutoPageSize";
import { setCurrentView } from "../../../state/reducers/viewReducer";
import { RootState } from "../../../state/store";
import { DiggingLoadingSummaryDto } from "../../../DTOs/Production/DiggingLoadingSummaryDto";
import { setSelectedDate, setSelectedShift } from "../../../state/reducers/shiftReducer";
import useShiftTimes from "../../../components/Hooks/useShiftTimes";
import { diggingAjax } from "../../../ajax/DiggingAjax";
import DiggingLoadingSummaryTableFooter from "./DiggingLoadingSummaryTableFooter";
import { GetDiggingSummaryPagedResult } from "../../../DTOs/Production/GetDiggingSummaryPagedResultDTO";
import DiggingLoadingSummaryTableNoResultsContent from "./DiggingLoadingSummaryTableNoResultsContent";
import { useSignalRContext } from "../../../components/Contexts/SignalRContext";
import { TablePage } from "../../../components/Layout/TablePage";
import { viewName } from "../../../i18n/pageNames";

type GetDiggingSummaryTableMetaData = {
  totalLoads: number;
  totalTonnes: number;
}

const DiggingLoadingSummaryPage: React.FC = () => {
  const { t } = useTranslation("production");
  const dispatch = useDispatch();
  const currentView = useSelector((s: RootState) => s.view.currentView);
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();
  const { enqueueSnackbar } = useSnackbar();

  const getRows = useCallback(
    (req: DataTableFilteredRequest) => {
      return diggingAjax.getDiggingLoadingSummary(selectedDate, selectedShift, req);
    },
    [selectedDate, selectedShift]
  );

  const translateRows = useCallback(
    (entry: DiggingLoadingSummaryDto): TableRow => {
      return {
        id: entry.diggingPatternId ?? entry.diggingId,
        values: [
          {
            name: "Select",
            label: t("actions.select"),
            type: "integer",
            value: entry.diggingPatternId,
            tailwindClasses: "basis-[12%] py-2",
            render: () => (
              <Link
                to={`/production/show?diggingPatternId=${entry.diggingPatternId}`}
                className="px-3 py-1 rounded bg-theme-info hover:bg-theme-info-hover text-white transition"
              >
                {t("actions.select")}
              </Link>
            ),
          },
          { name: "Excavator", label: t("columns.excavator"), type: "string", value: entry.excavator, tailwindClasses: "basis-[15%] py-2" },
          { name: "Pit Stage", label: t("fields.pitStage"), type: "string", value: entry.pitStage, tailwindClasses: "basis-[15%] py-2" },
          { name: "Blast Master Id", label: t("columns.blastMasterId"), type: "string", value: entry.blastMasterId, tailwindClasses: "basis-[23%] py-2" },
          {
            name: "Start Time",
            label: t("fields.startTime"),
            type: "date",
            value: entry.startTime,
            tailwindClasses: "basis-[13%] py-2",
            render: () => <span>{entry.startTime.toFormat('HH:mm')}</span>,
          },
          { name: "Loads", label: t("fields.loads"), type: "integer", value: entry.loads, tailwindClasses: "basis-[11%] py-2" },
          {
            name: "Tonnes",
            label: t("fields.tonnes"),
            type: "integer",
            value: entry.tonnes,
            tailwindClasses: "basis-[11%] py-2",
            render: (tv) => <span>{formatNumber(Math.round(Number(tv.value || 0)))}</span>
          },
        ],
      };
    },
    [t]
  );

  const getDiggingLoadingSummaryTableMetaData = useCallback((result: GetDiggingSummaryPagedResult) => ({
    totalLoads: result.totalLoads,
    totalTonnes: result.totalTonnes,
  }), []);

  const { pageSize: fitPageSize, onAutoPageSize } = useAutoPageSize();

  const {
    rows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    setPage,
    sortField,
    sortDirection,
    totalCount,
    pageSize,
    tableMetaData,
    updateRowSource,
    appendRowSource,
    removeRowSource
  } = useDataTable<DiggingLoadingSummaryDto, GetDiggingSummaryTableMetaData, GetDiggingSummaryPagedResult>({
    fetchFn: getRows,
    queryKey: ['chatree', 'diggingLoadingSummary', selectedDate.toISODate(), selectedShift],
    initialPage: 1,
    pageSize: fitPageSize,
    clientPaginate: true,
    selectMeta: getDiggingLoadingSummaryTableMetaData
  });

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);

  const displayDataRef = useRef(displayData);
  useEffect(() => { displayDataRef.current = displayData; }, [displayData]);

  useEffect(() => {
    if (currentView !== "DiggingLoadingSummary") {
      dispatch(setCurrentView("DiggingLoadingSummary"));
    }
  }, [currentView, dispatch]);

  const handleDiggingLocationUpdated = useCallback((updatedSummary: DiggingLoadingSummaryDto) => {
    if (!updatedSummary?.diggingPatternId) return;

    updateRowSource(
      row => row.diggingPatternId === updatedSummary.diggingPatternId,
      updatedSummary
    );
    enqueueSnackbar(updatedSummary.excavator != null ? t("summary.excavatorUpdated", { name: updatedSummary.excavator }) : t("summary.patternUpdated"), { variant: "info", autoHideDuration: 3000 });
  }, [updateRowSource, enqueueSnackbar, t]);

  const handleDiggingLocationInserted = useCallback((newSummary: DiggingLoadingSummaryDto) => {
    if (!newSummary?.diggingPatternId) return;

    const existingRow = displayDataRef.current.find(row => row.id === newSummary.diggingPatternId);

    if (existingRow) {
      updateRowSource(
        row => row.diggingPatternId === newSummary.diggingPatternId,
        newSummary
      );
    } else {
      appendRowSource(newSummary);
    }

    enqueueSnackbar(newSummary.excavator != null ? t("summary.excavatorAdded", { name: newSummary.excavator }) : t("summary.patternAdded"), { variant: "info", autoHideDuration: 3000 });
  }, [updateRowSource, appendRowSource, enqueueSnackbar, t]);

  const handleDiggingLocationDeleted = useCallback((deletedSummary: DiggingLoadingSummaryDto) => {
    if (!deletedSummary?.diggingPatternId) return;

    if (deletedSummary.loads === 0 && deletedSummary.tonnes === 0) {
      removeRowSource(row => row.diggingPatternId === deletedSummary.diggingPatternId);
    } else {
      updateRowSource(
        row => row.diggingPatternId === deletedSummary.diggingPatternId,
        deletedSummary
      );
    }

    enqueueSnackbar(deletedSummary.excavator != null ? t("summary.excavatorRemoved", { name: deletedSummary.excavator }) : t("summary.patternRemoved"), { variant: "info", autoHideDuration: 3000 });
  }, [updateRowSource, removeRowSource, enqueueSnackbar, t]);

  useEffect(() => {
    if (!connection) return;

    connection.on("DataUpdatedAsync", handleDiggingLocationUpdated);
    connection.on("DataInsertedAsync", handleDiggingLocationInserted);
    connection.on("DataDeletedAsync", handleDiggingLocationDeleted);

    return () => {
      connection.off("DataUpdatedAsync", handleDiggingLocationUpdated);
      connection.off("DataInsertedAsync", handleDiggingLocationInserted);
      connection.off("DataDeletedAsync", handleDiggingLocationDeleted);
    };
  }, [connection, handleDiggingLocationUpdated, handleDiggingLocationInserted, handleDiggingLocationDeleted]);

  return (
    <TablePage>
      <div className="shrink-0">
        <Breadcrumbs>
          <DateSelectCrumb
            selectedDate={selectedDate}
            setSelectedDate={(d) => dispatch(setSelectedDate(d.toISODate()!))}
          />
          <ShiftSelectCrumb
            selectedShift={selectedShift}
            setSelectedShift={(s) => dispatch(setSelectedShift(s))}
          />
          <ButtonCrumb buttonText={viewName("DiggingLoadingSummary")} />
        </Breadcrumbs>
      </div>
      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        error={error}
        pageNumber={pageNumber}
        setPage={setPage}
        pageSize={pageSize}
        totalCount={totalCount}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1080}
        fillHeight
        autoPageSize
        onAutoPageSize={onAutoPageSize}
        estimatedRowHeightPx={52}
        noResultsContent={<DiggingLoadingSummaryTableNoResultsContent />}
        tableFooterContent={<DiggingLoadingSummaryTableFooter
          isLoading={isLoading}
          loads={tableMetaData?.totalLoads}
          tonnes={tableMetaData?.totalTonnes}
        />}
      />
    </TablePage>
  );
};

export default DiggingLoadingSummaryPage;